import React, { useEffect, useState } from 'react';
import { Check, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { ChannelCheck, EntityItem, Fact, OwnerComment, PendingFact, Question, SectionKey, Strategy } from '../../../services/brandKnowledgeApi';

/**
 * Shared pieces for the brand knowledge parts of Brand Kit:
 * structured facts (products, customer groups, claims, never-say), confirmations,
 * missing questions, and the strategy proposal with per-section approval.
 */

export const SECTION_ORDER: { type: string; title: string; addLabel?: string }[] = [
  { type: 'brand', title: 'Business' },
  { type: 'offering', title: 'Products and services', addLabel: 'Add product or service' },
  { type: 'segment', title: 'Customer groups', addLabel: 'Add customer group' },
  { type: 'claim', title: 'Claims and proof points', addLabel: 'Add claim' },
  { type: 'never_say', title: 'Never say', addLabel: 'Add rule' },
  { type: 'voice', title: 'Voice and tone' },
];

export const STRATEGY_SECTIONS: { key: SectionKey; title: string }[] = [
  { key: 'brand_summary', title: 'Brand summary' },
  { key: 'positioning', title: 'Positioning' },
  { key: 'priority_segments', title: 'Priority customers' },
  { key: 'markets', title: 'Where to advertise' },
  { key: 'offerings_to_lead', title: 'Products to push' },
  { key: 'message_pillars', title: 'Key messages' },
  { key: 'voice', title: 'Voice' },
  { key: 'channels', title: 'Channels and budget split' },
  { key: 'creative_approach', title: 'Creative approach' },
  { key: 'goals', title: 'Goal' },
  { key: 'assumptions', title: 'Assumptions to test' },
  { key: 'open_questions', title: 'Questions for you' },
];

export const SOURCE_TEXT: Record<string, string> = {
  owner: 'From you', website: 'From your website', brand_fact: 'From brand facts',
  recommendation: 'Our recommendation', assumption: 'Our assumption',
};

// Friendly labels for strategy fields. `null` = show the value on its own (the item's headline).
export const FIELD_LABELS: Record<string, string | null> = {
  value: null, name: null, message: null, platform: null, statement: null, question: null,
  description: null, decision_maker: 'Decides', main_objections: 'Hesitations', proof_points: 'Proof',
  role: 'Role', tone: 'Tone', language: 'Language', use_words: 'Use', avoid_words: 'Avoid',
  budget_share_pct: 'Budget share', primary_goal: 'Goal', kpi: 'Measure',
  optimise_for: 'Ads optimise for', volume_check: 'Expected volume', first_target: 'First target',
  timeframe_days: 'Review after (days)', how_to_test: 'How to test', why_it_matters: 'Why it matters',
};
const ROLE_TEXT: Record<string, string> = { start: 'Start here', later: 'Later' };
export const HEADLINE_KEYS = new Set(['name', 'message', 'platform', 'statement', 'question']);
export const fieldLabel = (k: string) => (k in FIELD_LABELS ? FIELD_LABELS[k] : k.replace(/_/g, ' '));

// Fields of strategy items the owner never edits directly.
export const HIDDEN_KEYS = new Set(['reason', 'source', 'confidence', 'fact_refs', 'fact_ids', 'offering_ref', 'offering_fact_id', 'offering_key']);

export const show = (v: any): string => (Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v ?? ''));
export const errText = (e: any): string => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return e?.message || 'Something went wrong. Please try again.';
};

// ─── Pieces ─────────────────────────────────────────────────────────────────

export const PendingList: React.FC<{
  items: PendingFact[]; busy: string | null; onConfirm: (p: PendingFact) => void; onReject: (p: PendingFact) => void;
}> = ({ items, busy, onConfirm, onReject }) => (
  <div className="rounded-lg border border-warning/40 bg-warning-soft p-3 space-y-2">
    <p className="text-sm font-medium text-ink">Please confirm these before we use them in ads</p>
    {items.map(p => (
      <div key={p.id} className="flex items-start justify-between gap-3 text-sm">
        <div className="min-w-0">
          <span className="text-ink-2">{p.label}: </span>
          <span className="text-ink break-words">{show(p.value)}</span>
          <span className="text-xs text-ink-3"> · {p.source_label}</span>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <Button size="xs" variant="primary" loading={busy === `c-${p.id}`} onClick={() => onConfirm(p)}>Correct</Button>
          <Button size="xs" variant="ghost" loading={busy === `r-${p.id}`} onClick={() => onReject(p)}>Wrong</Button>
        </div>
      </div>
    ))}
  </div>
);

export const QuestionsForm: React.FC<{
  questions: Question[]; steps?: { id: string; title: string }[]; busy: boolean;
  onSubmit: (answers: { entity_type: string; attribute: string; value: any; entity_key?: string | null }[], newEntities: Record<string, string[]>) => void;
}> = ({ questions, steps, busy, onSubmit }) => {
  const [values, setValues] = useState<Record<string, string>>({});
  // Pre-fill what we already found (Brand Kit, website); the owner checks and saves.
  useEffect(() => {
    setValues(prev => {
      const next = { ...prev };
      questions.forEach(q => { if (q.suggested && next[q.id] === undefined) next[q.id] = q.suggested; });
      return next;
    });
  }, [questions]);
  const submit = () => {
    const answers: any[] = [];
    const newEntities: Record<string, string[]> = {};
    questions.forEach(q => {
      const v = (values[q.id] || '').trim();
      if (!v) return;
      if (q.multi_entity) newEntities[q.entity_type] = v.split('\n').map(s => s.trim()).filter(Boolean);
      else answers.push({ entity_type: q.entity_type, entity_key: q.entity_key, attribute: q.attribute, value: v });
    });
    onSubmit(answers, newEntities);
  };
  const field = (q: Question) => (
    <label key={q.id} className="block text-sm">
      <span className="text-ink-2">{q.question}</span>
      {q.optional && <span className="ml-1 text-xs text-ink-3">(optional)</span>}
      {q.suggested && values[q.id] === q.suggested && (
        <span className="ml-1 text-xs text-ink-3">· found {q.suggested_from}, check and save</span>
      )}
      {q.vtype === 'enum' ? (
        <select
          className="mt-1 w-full rounded-lg border border-border bg-input px-3 py-2 text-ink"
          value={values[q.id] || ''} onChange={e => setValues({ ...values, [q.id]: e.target.value })}
        >
          <option value="">Choose…</option>
          {q.choices.map(c => <option key={c} value={c}>{q.choice_labels?.[c] || c.replace(/_/g, ' ')}</option>)}
        </select>
      ) : q.multi_entity || q.vtype === 'text' ? (
        <textarea
          rows={q.multi_entity ? 3 : 2}
          className="mt-1 w-full rounded-lg border border-border bg-input px-3 py-2 text-ink"
          placeholder={q.multi_entity ? 'One per line' : ''}
          value={values[q.id] || ''} onChange={e => setValues({ ...values, [q.id]: e.target.value })}
        />
      ) : (
        <input
          type={q.vtype === 'number' ? 'number' : 'text'}
          className="mt-1 w-full rounded-lg border border-border bg-input px-3 py-2 text-ink"
          placeholder={q.vtype === 'list' ? 'Separate with commas' : ''}
          value={values[q.id] || ''} onChange={e => setValues({ ...values, [q.id]: e.target.value })}
        />
      )}
    </label>
  );
  // Group by onboarding step; questions without a step go last.
  const groups = (steps && steps.length ? steps : [{ id: '', title: '' }]).map(s => ({
    ...s, items: questions.filter(q => (steps && steps.length ? (q.step || '') === s.id : true)),
  })).filter(g => g.items.length);
  const stray = steps && steps.length ? questions.filter(q => !steps.some(s => s.id === q.step)) : [];
  return (
    <div className="space-y-4">
      <p className="text-sm font-medium text-ink">A few questions</p>
      {groups.map(g => (
        <div key={g.id || 'all'} className="space-y-3">
          {g.title && <p className="text-xs font-semibold uppercase tracking-wide text-ink-3">{g.title}</p>}
          {g.items.map(field)}
        </div>
      ))}
      {stray.map(field)}
      <Button variant="primary" loading={busy} onClick={submit}>Save answers</Button>
    </div>
  );
};

export const Understood: React.FC<{
  entities: Record<string, EntityItem[]>; busy: string | null; types?: string[]; title?: string;
  onEdit: (f: Fact, value: string) => void; onRemoveFact: (f: Fact) => void;
  onRemoveEntity: (type: string, key: string) => void; onAdd: (type: string, name: string) => void;
}> = ({ entities, busy, types, title, onEdit, onRemoveFact, onRemoveEntity, onAdd }) => (
  <div className="space-y-4">
    {title && <p className="text-sm font-medium text-ink">{title}</p>}
    {SECTION_ORDER.filter(sec => !types || types.includes(sec.type)).map(sec => {
      const items = entities[sec.type] || [];
      if (!items.length && !sec.addLabel) return null;
      return (
        <div key={sec.type} className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-3">{sec.title}</p>
          {items.map(e => (
            <div key={e.id} className="rounded-lg border border-border p-2.5 space-y-1">
              {Object.values(e.facts).map(f => (
                <FactRow key={f.id} fact={f} busy={busy} onEdit={onEdit} onRemove={onRemoveFact} />
              ))}
              {sec.type !== 'brand' && sec.type !== 'voice' && (
                <button className="text-xs text-ink-3 hover:text-danger inline-flex items-center gap-1" onClick={() => onRemoveEntity(sec.type, e.key)}>
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
          ))}
          {sec.addLabel && <AddItem label={sec.addLabel} busy={busy === `a-${sec.type}`} onAdd={name => onAdd(sec.type, name)} />}
        </div>
      );
    })}
  </div>
);

export const FactRow: React.FC<{ fact: Fact; busy: string | null; onEdit: (f: Fact, v: string) => void; onRemove: (f: Fact) => void }> = ({ fact, busy, onEdit, onRemove }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(show(fact.value));
  return (
    <div className="flex items-start justify-between gap-2 text-sm">
      <div className="min-w-0 flex-1">
        <span className="text-ink-2">{fact.label}: </span>
        {editing ? (
          <span className="inline-flex items-center gap-1 w-full">
            <input className="flex-1 rounded border border-border bg-input px-2 py-1 text-ink" value={val} onChange={e => setVal(e.target.value)} aria-label={fact.label} />
            <button aria-label="Save" onClick={() => { onEdit(fact, val); setEditing(false); }}><Check className="w-4 h-4 text-success" /></button>
            <button aria-label="Cancel" onClick={() => { setVal(show(fact.value)); setEditing(false); }}><X className="w-4 h-4 text-ink-3" /></button>
          </span>
        ) : (
          <span className="text-ink break-words">{show(fact.value)}</span>
        )}
        {!editing && (
          <span className={`ml-1.5 text-xs ${fact.status === 'confirmed' ? 'text-success' : 'text-ink-3'}`}>
            {fact.status === 'confirmed' ? '✓ confirmed' : fact.source_label}
          </span>
        )}
      </div>
      {!editing && (
        <div className="flex gap-1 flex-shrink-0">
          <button aria-label={`Edit ${fact.label}`} className="text-ink-3 hover:text-primary" onClick={() => setEditing(true)}><Pencil className="w-3.5 h-3.5" /></button>
          {busy === `r-${fact.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
            <button aria-label={`Mark ${fact.label} as wrong`} className="text-ink-3 hover:text-danger" onClick={() => onRemove(fact)}><X className="w-3.5 h-3.5" /></button>
          )}
        </div>
      )}
    </div>
  );
};

export const AddItem: React.FC<{ label: string; busy: boolean; onAdd: (name: string) => void }> = ({ label, busy, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  if (!open) {
    return <button className="text-xs text-primary inline-flex items-center gap-1" onClick={() => setOpen(true)}><Plus className="w-3 h-3" /> {label}</button>;
  }
  return (
    <div className="flex gap-2">
      <input autoFocus className="flex-1 rounded-lg border border-border bg-input px-3 py-1.5 text-sm text-ink" value={name} onChange={e => setName(e.target.value)} aria-label={label} />
      <Button size="sm" variant="primary" loading={busy} disabled={!name.trim()} onClick={() => { onAdd(name.trim()); setName(''); setOpen(false); }}>Add</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  );
};

// ─── Strategy ───────────────────────────────────────────────────────────────

export const StrategyView: React.FC<{
  strategy: Strategy; factsById: Record<string, Fact>; busy: string | null;
  onRefresh: () => void; onApproveAll: () => void; onApproveSection: (k: SectionKey) => void;
  onSaveSection: (k: SectionKey, v: any) => void; onRevise?: (comments: string) => Promise<unknown>;
}> = ({ strategy, factsById, busy, onRefresh, onApproveAll, onApproveSection, onSaveSection, onRevise }) => {
  const approved = strategy.status === 'approved';
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ink-2">
          Version {strategy.version} · {approved ? 'Approved — campaigns and Ad Studio use this' : 'Waiting for your approval'}
        </p>
        {!approved && <Button variant="primary" loading={busy === 'approve'} onClick={onApproveAll}>Approve strategy</Button>}
      </div>
      {strategy.stale && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-info/40 bg-info-soft p-3 text-sm">
          <span className="text-ink">Your brand facts changed since this strategy was made.</span>
          <Button size="sm" variant="secondary" loading={busy === 'propose'} onClick={onRefresh}><RefreshCw className="w-3.5 h-3.5 mr-1" />Update strategy</Button>
        </div>
      )}
      {onRevise && (
        <RevisePanel what="strategy" busy={busy === 'revise' || busy === 'propose'}
          notes={strategy.content.revision_notes} history={strategy.content.owner_feedback} onSubmit={onRevise} />
      )}
      {STRATEGY_SECTIONS.map(({ key, title }) => (
        <React.Fragment key={key}>
          <StrategySection
            title={title} sectionKey={key} value={strategy.content[key]}
            status={strategy.section_status[key]} factsById={factsById} busy={busy}
            onApprove={() => onApproveSection(key)} onSave={v => onSaveSection(key, v)}
          />
          {key === 'channels' && !!strategy.content.channel_evaluation?.length && (
            <ChannelReasons items={strategy.content.channel_evaluation} pastResults={strategy.content.past_results} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export const StrategySection: React.FC<{
  title: string; sectionKey: SectionKey; value: any; status?: string; factsById: Record<string, Fact>;
  busy: string | null; onApprove: () => void; onSave: (v: any) => void;
}> = ({ title, sectionKey, value, status, factsById, busy, onApprove, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<any>(value);
  useEffect(() => { setDraft(value); }, [value]);
  const items: any[] = Array.isArray(value) ? value : sectionKey === 'positioning' ? Object.entries(value || {}).map(([k, v]: any) => ({ ...v, _label: k.replace(/_/g, ' ') })) : [value];

  const save = () => { onSave(draft); setEditing(false); };

  // The creative approach is a nested structure: change it with "Ask for changes".
  const editable = sectionKey !== 'creative_approach';
  if (value == null || (Array.isArray(value) && !value.length && sectionKey === 'markets')) return null;

  return (
    <div className="rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-base font-bold text-ink">{title}</h3>
        <div className="flex items-center gap-2">
          {status === 'approved' && <span className="text-xs font-semibold text-success inline-flex items-center gap-1"><Check className="w-3 h-3" />Approved</span>}
          {status === 'edited' && <span className="text-xs text-ink-3">Edited</span>}
          {!editing && editable && <button className="text-xs text-ink-3 hover:text-primary inline-flex items-center gap-1" onClick={() => setEditing(true)}><Pencil className="w-3 h-3" />Edit</button>}
          {!editing && status !== 'approved' && <Button size="xs" variant="secondary" loading={busy === `as-${sectionKey}`} onClick={onApprove}>Approve</Button>}
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <SectionEditor sectionKey={sectionKey} draft={draft} setDraft={setDraft} />
          <div className="flex gap-2">
            <Button size="sm" variant="primary" loading={busy === `es-${sectionKey}`} onClick={save}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setDraft(value); setEditing(false); }}>Cancel</Button>
          </div>
        </div>
      ) : sectionKey === 'creative_approach' ? (
        <CreativeApproachView value={value} factsById={factsById} />
      ) : (
        items.map((it, i) => <StrategyItem key={i} item={it} factsById={factsById} />)
      )}
    </div>
  );
};

const STYLE_TEXT: Record<string, { label: string; hint: string }> = {
  finished_ad: { label: 'Finished ads', hint: 'Designed in Ad Studio from your photos, or your own ads, with the message in the image' },
  dynamic_creative: { label: 'Dynamic creative', hint: 'Plain images plus text options that Meta combines and tests' },
  search_text: { label: 'Search ads', hint: 'Text ads shown to people searching on Google' },
};

/** Strategy · creative approach: how each channel's ads look, and what to test. */
const CreativeApproachView: React.FC<{ value: any; factsById: Record<string, Fact> }> = ({ value, factsById }) => (
  <div className="space-y-3">
    <div className="divide-y divide-border rounded-lg border border-border">
      {(value.channels || []).map((c: any) => (
        <div key={c.platform} className="grid gap-2 p-3 sm:grid-cols-[9rem_1fr]">
          <span className="font-semibold text-ink">{CHANNEL_LABEL[c.platform] || c.platform}</span>
          <div className="min-w-0 space-y-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="rounded-full bg-primary-soft px-2.5 py-0.5 text-xs font-semibold text-primary-dark">{STYLE_TEXT[c.style]?.label || c.style}</span>
              {(c.formats || []).map((f: string) => (
                <span key={f} className="rounded-full bg-surface px-2.5 py-0.5 text-xs font-medium text-ink-2">
                  {f === 'text' ? 'Text' : f}{f === '9:16' && c.motion ? ' · motion' : ''}
                </span>
              ))}
            </div>
            <p className="text-xs text-ink-3">{STYLE_TEXT[c.style]?.hint}</p>
          </div>
        </div>
      ))}
    </div>
    <dl className="grid grid-cols-1 sm:grid-cols-[9rem_1fr] gap-x-3 gap-y-1.5 text-sm">
      <dt className="text-ink-3">Ads to test</dt><dd className="text-ink">{value.variants_per_campaign} per campaign</dd>
      <dt className="text-ink-3">Your media</dt><dd className="text-ink">{value.use_own_media}</dd>
      {value.style_notes && (<><dt className="text-ink-3">Look and feel</dt><dd className="text-ink">{value.style_notes}</dd></>)}
    </dl>
    <StrategyItem item={{ reason: value.reason, source: value.source, confidence: value.confidence, fact_ids: value.fact_ids }} factsById={factsById} />
  </div>
);

export const StrategyItem: React.FC<{ item: any; factsById: Record<string, Fact> }> = ({ item, factsById }) => {
  if (!item) return null;
  const offering = item.offering_fact_id ? factsById[item.offering_fact_id]?.value : null;
  const fields = Object.entries(item).filter(([k]) => !HIDDEN_KEYS.has(k) && !k.startsWith('_'));
  const basedOn = (item.fact_ids || []).map((id: string) => factsById[id]).filter(Boolean) as Fact[];
  return (
    <div className="text-sm space-y-0.5">
      {item._label && <p className="text-xs uppercase tracking-wide text-ink-3">{item._label}</p>}
      {offering && <p className="text-ink font-medium">{show(offering)}</p>}
      {fields.map(([k, v]) => {
        if (v === '' || (Array.isArray(v) && !v.length)) return null;
        const label = fieldLabel(k);
        return (
          <p key={k} className={HEADLINE_KEYS.has(k) ? 'text-ink font-medium' : 'text-ink'}>
            {label && <span className="text-ink-2 font-normal">{label}: </span>}
            {k === 'budget_share_pct' ? (String(item.platform || '').endsWith('_organic') ? 'No ad budget (regular posts)' : `${v}%`) : k === 'role' ? (ROLE_TEXT[String(v)] || show(v).replace(/_/g, ' ')) : k === 'primary_goal' ? show(v).replace(/_/g, ' ') : show(v)}
          </p>
        );
      })}
      {item.reason && (
        <p className="text-xs text-ink-3">
          <span className={item.source === 'assumption' ? 'text-warning' : 'text-ink-2'}>{SOURCE_TEXT[item.source] || item.source}</span>
          {' · '}{item.confidence} confidence · {item.reason}
          {basedOn.length > 0 && <> · based on: {basedOn.map(f => `${f.label} (${show(f.value)})`).join('; ')}</>}
        </p>
      )}
    </div>
  );
};

/** Simple editor: strings → text inputs, string lists → comma separated, numbers → number inputs. */
export const SectionEditor: React.FC<{ sectionKey: SectionKey; draft: any; setDraft: (v: any) => void }> = ({ sectionKey, draft, setDraft }) => {
  const editItem = (item: any, onChange: (v: any) => void) => (
    <div className="rounded border border-border p-2 space-y-1.5">
      {Object.entries(item || {}).filter(([k]) => !HIDDEN_KEYS.has(k)).map(([k, v]) => (
        <label key={k} className="block text-xs text-ink-2">
          {fieldLabel(k) || k.replace(/_/g, ' ')}
          <input
            className="mt-0.5 w-full rounded border border-border bg-input px-2 py-1 text-sm text-ink"
            type={typeof v === 'number' ? 'number' : 'text'}
            value={Array.isArray(v) ? v.join(', ') : String(v ?? '')}
            onChange={e => {
              const raw = e.target.value;
              const next = Array.isArray(v) ? raw.split(',').map(s => s.trim()).filter(Boolean) : typeof v === 'number' ? Number(raw) : raw;
              // An owner edit is the owner's own decision.
              onChange({ ...item, [k]: next, ...(item.source ? { source: 'owner', confidence: 'high' } : {}) });
            }}
          />
        </label>
      ))}
    </div>
  );
  if (Array.isArray(draft)) {
    return <div className="space-y-2">{draft.map((it, i) => <div key={i}>{editItem(it, v => setDraft(draft.map((x: any, j: number) => (j === i ? v : x))))}</div>)}</div>;
  }
  if (sectionKey === 'positioning') {
    return (
      <div className="space-y-2">
        {Object.entries(draft || {}).map(([k, it]) => (
          <div key={k}><p className="text-xs uppercase tracking-wide text-ink-3">{k.replace(/_/g, ' ')}</p>{editItem(it, v => setDraft({ ...draft, [k]: v }))}</div>
        ))}
      </div>
    );
  }
  return editItem(draft, setDraft);
};


/** "What your past ads tell us": read-only, computed from the connected ad accounts. */
export const PastAds: React.FC<{ entities: Record<string, EntityItem[]> }> = ({ entities }) => {
  const facts = entities.performance?.[0]?.facts;
  if (!facts) return null;
  const list = (k: string, title: string) => {
    const items: string[] = facts[k]?.value || [];
    if (!items.length) return null;
    return (
      <div>
        <div className="text-xs font-medium text-ink-3 uppercase tracking-wide mb-1">{title}</div>
        <ul className="space-y-1">
          {items.map((t, i) => <li key={i} className="text-sm text-ink-2">• {t}</li>)}
        </ul>
      </div>
    );
  };
  return (
    <div className="rounded-lg border border-border bg-surface/50 p-4 space-y-3">
      <div className="text-sm font-semibold text-ink">What your past ads tell us</div>
      {facts.history && <p className="text-sm text-ink-2">{show(facts.history.value)}</p>}
      {list('by_channel', 'By channel and goal')}
      {list('best_campaigns', 'Worked best')}
      {list('weak_campaigns', 'Worked worst')}
      <p className="text-xs text-ink-3">From your connected ad accounts. Updated monthly; your strategy uses it.</p>
    </div>
  );
};

export const CHANNEL_LABEL: Record<string, string> = {
  meta: 'Meta ads', google_ads: 'Google Ads', pinterest: 'Pinterest ads', twitter: 'X ads', tiktok: 'TikTok ads',
  instagram_organic: 'Instagram posts', facebook_organic: 'Facebook posts',
};
const VERDICT: Record<ChannelCheck['verdict'], { text: string; cls: string }> = {
  use: { text: 'Use', cls: 'border-success/40 bg-success-soft text-success' },
  test_later: { text: 'Test later', cls: 'border-info/40 bg-info-soft text-info' },
  skip: { text: 'Skip', cls: 'border-border text-ink-3' },
};
const CHECKS: [keyof ChannelCheck, string][] = [
  ['demand', 'Demand'], ['reach', 'Reach'], ['format_fit', 'Format'], ['path_to_goal', 'Path to goal'],
  ['budget', 'Budget'], ['past_results', 'Past results'],
];

/** Why each allowed channel was used, kept for later or skipped: the same questions for every channel. */
const PAST_RESULTS: Record<string, string> = {
  use: 'Your past ad results guide these choices', weak: 'Your past ad results are only an early signal',
  ignore: 'Your past ad results are set aside', none: 'No past ad results yet',
};

export const ChannelReasons: React.FC<{ items: ChannelCheck[]; pastResults?: { use: string; reason: string } }> = ({ items, pastResults }) => {
  const [open, setOpen] = useState<string | null>(null);
  const order = { use: 0, test_later: 1, skip: 2 };
  return (
    <div className="rounded-lg border border-border p-3.5 space-y-2">
      <h3 className="text-sm font-semibold text-ink">Why these channels</h3>
      {pastResults && pastResults.use !== 'none' && (
        <p className="text-xs text-ink-2">{PAST_RESULTS[pastResults.use] || pastResults.use}: {pastResults.reason}</p>
      )}
      {[...items].sort((a, b) => order[a.verdict] - order[b.verdict]).map(e => (
        <div key={e.platform} className="text-sm">
          <div className="flex items-start gap-2">
            <span className={`flex-shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${VERDICT[e.verdict].cls}`}>
              {VERDICT[e.verdict].text}
            </span>
            <div className="min-w-0">
              <span className="font-medium text-ink">{CHANNEL_LABEL[e.platform] || e.platform}</span>
              <span className="text-ink-2"> · {e.reason}</span>
              <button className="ml-2 text-xs text-ink-3 hover:text-primary" onClick={() => setOpen(open === e.platform ? null : e.platform)}>
                {open === e.platform ? 'Hide' : 'Details'}
              </button>
            </div>
          </div>
          {open === e.platform && (
            <dl className="mt-1 ml-14 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
              {CHECKS.map(([k, label]) => (
                <React.Fragment key={k}><dt className="text-ink-3">{label}</dt><dd className="text-ink-2">{String(e[k])}</dd></React.Fragment>
              ))}
            </dl>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Ask for changes in your own words: the model revises the whole strategy or plan for them
 * (instead of editing fields one by one), says what it changed, and keeps the comments for
 * later versions.
 */
export const RevisePanel: React.FC<{
  what: 'strategy' | 'plan'; busy: boolean; notes?: string[]; history?: OwnerComment[];
  onSubmit: (comments: string) => Promise<unknown> | void;
}> = ({ what, busy, notes, history, onSubmit }) => {
  const [text, setText] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const submit = async () => {
    const t = text.trim();
    if (t.length < 3) return;
    await onSubmit(t);
    setText('');
  };
  return (
    <div className="space-y-3">
      {!!notes?.length && (
        <div className="rounded-lg border border-success/40 bg-success-soft p-3 text-sm">
          <p className="font-medium text-ink">What changed based on your comments</p>
          <ul className="mt-1 list-disc pl-5 text-ink-2 space-y-0.5">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
      )}
      <div className="rounded-lg border border-border p-3 space-y-2">
        <p className="text-sm font-medium text-ink">Ask for changes</p>
        <textarea
          rows={3} value={text} onChange={e => setText(e.target.value)} disabled={busy}
          className="w-full rounded-lg border border-border bg-input px-3 py-2 text-sm text-ink"
          placeholder={what === 'strategy'
            ? 'e.g. "Focus on D2C brands first", "We can\'t serve the US yet", "Put more weight on the free trial"'
            : 'e.g. "Launch the Diwali campaign a week earlier", "Fewer posts, more reels", "Split Meta into two tests"'}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" variant="primary" loading={busy} disabled={text.trim().length < 3} onClick={submit}>
            Revise {what}
          </Button>
          {busy && <span className="text-xs text-ink-3">Revising your {what}. This usually takes one to three minutes.</span>}
          {!!history?.length && (
            <button className="text-xs text-ink-3 hover:text-primary" onClick={() => setShowHistory(!showHistory)}>
              {showHistory ? 'Hide' : 'Show'} your earlier comments ({history.length})
            </button>
          )}
        </div>
        {showHistory && (
          <ul className="text-xs text-ink-2 space-y-1">
            {history!.map((h, i) => <li key={i}>“{h.text}” <span className="text-ink-3">· on version {h.on_version}</span></li>)}
          </ul>
        )}
        <p className="text-xs text-ink-3">Your comments are kept: later versions follow them too.</p>
      </div>
    </div>
  );
};
